<script setup>
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  reactive,
  ref,
  computed,
  watchEffect,
  watch,
  onMounted,
  getCurrentInstance,
} from "vue";
import apiCall from "@/utils/api";
import {
  debounce,
  useRoute,
  useRouter,
  watchRoute,
} from "@/components/Utils/functions";
import Pagination from "@/components/system/Pagination.vue";
import LoadingArea from "@/components/Utils/LoadingArea.vue";
import DeleteDialog from "./DeleteDialog.vue";

const router = useRouter();
const currentRoute = useRoute();

const deleteOptions = reactive({
  itemToDelete: null,
  deleteDialogOpen: false,
});
const searchText = ref("");
const trimmedSearchText = computed(() => searchText.value.trim());
const hasSearchText = computed(() => !!trimmedSearchText.value);
const urlQueryHasSearchText = computed(() => !!currentRoute?.query?.search);

const pagination = reactive({
  currentPage: 1,
  itemsPerPage: 10,
  total: 0,
  firstPage: 1,
  lastPage: 1,
});

const initializeSearchAndPagination = (route) => {
  searchText.value = route?.query?.search ?? "";
  pagination.currentPage = !!route?.query?.page
    ? parseInt(route?.query?.page)
    : 1;
  pagination.itemsPerPage = !!route?.query?.items_per_page
    ? parseInt(route?.query?.items_per_page)
    : 10;
};
initializeSearchAndPagination(currentRoute)

const getQueryUrl = () => {
  const base = `/api/role?page=${pagination.currentPage}&items_per_page=${pagination.itemsPerPage}`;
  if (hasSearchText.value) {
    return `${base}&type=search&search=${trimmedSearchText.value}`;
  }
  return base;
};

const {
  isPending,
  isLoading,
  isFetching,
  isError,
  data: result,
  isStale,
  refetch,
} = useQuery({
  retry: false,
  // enabled: false,
  refetchOnWindowFocus: false,
  refetchOnMount: true,
  staleTime: 180000,
  queryKey: ["listRoles"],
  queryFn: async () => {
    const result = await apiCall({
      method: "GET",
      url: getQueryUrl(),
    });
    pagination.itemsPerPage = result.per_page;
    pagination.page = result.current_page;
    pagination.total = result.total;
    pagination.firstPage = result.from;
    pagination.lastPage = result.last_page;
    return result;
  },
});

const navigateWhenSearchOrPaginationChange = () => {
  const route = {
    name: "List Permissions",
    query: {
      items_per_page: pagination.itemsPerPage,
      page: pagination.currentPage,
    },
  };
  if (hasSearchText.value) {
    route.query.search = trimmedSearchText.value;
  }
  router.replace(route);
};

const debouncedChangeRoute = debounce(() =>
  navigateWhenSearchOrPaginationChange()
);

// Watches for changes in the url query then does the actual search
watchRoute((newRoute) => {
  initializeSearchAndPagination(newRoute);
  refetch();
});

// Just changes url query
watch(searchText, () => {
  pagination.currentPage = 1
  debouncedChangeRoute();
});

function onPaginationClicked(pageNumber) {
  pagination.currentPage = pageNumber;
  navigateWhenSearchOrPaginationChange();
}

function onEditClicked(role) {
  router.push({
    name: "Edit Role",
    params: {
      id: role.id,
    },
  });
}

function onViewClicked(role) {
  router.push({
    name: "Show Role",
    params: {
      id: role.id,
    },
  });
}

function onDeleteClicked(role) {
  deleteOptions.itemToDelete = role;
  deleteOptions.deleteDialogOpen = true;
}

const noResults = computed(
  () => !isLoading.value && !!!result.value?.data?.length
);
</script>

<template>
  <v-container class="pb-10">
    <v-row>
      <v-col>
        <v-card
          outlined
          class="mt-5 px-8 pt-10 pb-10"
          :style="{
            borderRadius: '25px',
          }"
        >
          <v-row>
            <v-col cols="12" md="auto">
              <h1>{{ $t("rolesMessage") }}</h1>
            </v-col>
            <v-spacer />
            <v-col cols="12" md="4">
              <v-text-field
                class="text_field header"
                outlined
                dense
                :label="$t('searchMessage')"
                append-icon="mdi-undo-variant"
                @click:append="searchText = ''"
                v-model="searchText"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="auto" class="d-flex justify-end">
              <v-btn
                depressed
                class="primary text-none"
                @click="router.push({ name: 'Create Role' })"
              >
                {{ $t("addRolesMessage") }}
                <v-icon right>mdi-plus-circle-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="mt-10">
            <v-col>
              <LoadingArea :loading="isFetching">
                <v-simple-table>
                  <template v-slot:default>
                    <thead class="header">
                      <tr class="tableHeader">
                        <th class="text-left text--text">
                          {{ $t("nameMessage") }}
                        </th>
                        <th class="text-left text--text">
                          {{ $t("roleCreationDateMsg") }}
                        </th>
                        <th class="text-right text--text">
                          {{ $tc("actionMsg", 2) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="noResults">
                        <td colspan="100%">
                          <div
                            class="d-flex align-center justify-center mt-6 mb-6"
                            :style="{ gap: '1em' }"
                          >
                            <v-icon large class="">
                              mdi-alert-circle-outline
                            </v-icon>
                            <span class="text-h6">
                              {{
                                urlQueryHasSearchText
                                  ? $t("noResultsMatchedSearchMsg")
                                  : $t("noResultsMsg")
                              }}
                            </span>
                          </div>
                        </td>
                      </tr>
                      <template v-else>
                        <tr v-for="permission in result?.data" :key="permission.id">
                          <td>
                            {{ permission.name }}
                          </td>
                          <td>
                            {{
                              permission.created_at
                                | moment("DD/MM/YYYY - hh:mm a")
                            }}
                          </td>
                          <td align="right">
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  class="button mr-1 universal--text"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="onViewClicked(permission)"
                                >
                                  <v-icon small> mdi-eye </v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t("viewMsg") }}</span>
                            </v-tooltip>
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  class="button mr-1 success--text"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="onEditClicked(permission)"
                                >
                                  <v-icon small> mdi-pencil </v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t("editMsg") }}</span>
                            </v-tooltip>

                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  class="button mr-1 error--text"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="onDeleteClicked(permission)"
                                >
                                  <v-icon small> mdi-archive </v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t("archiveMsg") }}</span>
                            </v-tooltip>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="100%">
                          <div class="d-flex justify-end pt-6">
                            <Pagination
                              :totalNumberOfItems="pagination.total"
                              :itemsPerPage="pagination.itemsPerPage"
                              :currentPage="pagination.currentPage"
                              @paginationClicked="onPaginationClicked"
                            />
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </template>
                </v-simple-table>
              </LoadingArea>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    
    <DeleteDialog
      v-if="!!deleteOptions.itemToDelete"
      :role="deleteOptions.itemToDelete"
      :dialogOpen.sync="deleteOptions.deleteDialogOpen"
    />
   
  </v-container>
</template>
